import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import SafeIcon from '../components/common/SafeIcon';
import TypeBadge from '../components/pokemon/TypeBadge';
import StatBar from '../components/pokemon/StatBar';
import PokemonCard from '../components/pokemon/PokemonCard';
import { getPokemonByName, getRelatedPokemon } from '../data/pokemonData';

const { FiArrowLeft, FiBarChart3, FiInfo, FiHeart, FiShield, FiZap, FiTarget } = FiIcons;

function PokemonDetailPage() {
  const { name } = useParams();
  const [pokemon, setPokemon] = useState(null);
  const [relatedPokemon, setRelatedPokemon] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPokemon = () => {
      setLoading(true);
      const pokemonData = getPokemonByName(name);
      
      if (pokemonData) {
        setPokemon(pokemonData);
        setRelatedPokemon(getRelatedPokemon(pokemonData));
      }
      
      setLoading(false);
    };

    fetchPokemon();
  }, [name]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Pokemon data...</p>
        </div>
      </div>
    );
  }

  if (!pokemon) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Pokemon Not Found</h1>
          <p className="text-gray-600 mb-6">The Pokemon you're looking for doesn't exist.</p>
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>
    );
  }

  const totalStats = Object.values(pokemon.baseStats).reduce((sum, stat) => sum + stat, 0);

  const pokemonSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${pokemon.displayName} Pokemon Stats, Abilities & Information`,
    "description": `Complete ${pokemon.displayName} Pokemon data including base stats, abilities, type effectiveness, and detailed information.`,
    "image": pokemon.image,
    "author": {
      "@type": "Organization",
      "name": "Pokemon Stats Database"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pokemon Stats Database",
      "logo": {
        "@type": "ImageObject",
        "url": "https://example.com/logo.png"
      }
    },
    "datePublished": "2024-01-01",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://example.com/pokemon/${pokemon.name}`
    }
  };

  const breadcrumbItems = [
    { name: 'Pokemon', path: '/pokemon' },
    { name: pokemon.displayName }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title={`${pokemon.displayName} Pokemon Stats, Abilities & Information`}
        description={`Complete ${pokemon.displayName} Pokemon data including base stats (HP: ${pokemon.baseStats.hp}, Attack: ${pokemon.baseStats.attack}, Defense: ${pokemon.baseStats.defense}), abilities, type effectiveness, and detailed information.`}
        keywords={`${pokemon.displayName}, pokemon stats, ${pokemon.types.join(', ')} type pokemon, ${pokemon.abilities.join(', ')}, pokemon database`}
        image={pokemon.image}
        url={`/pokemon/${pokemon.name}`}
        type="article"
        schema={pokemonSchema}
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Pokemon Header */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
                <div className="flex-shrink-0">
                  <img
                    src={pokemon.image}
                    alt={`${pokemon.displayName} official artwork`}
                    className="w-48 h-48 object-contain"
                  />
                </div>
                
                <div className="flex-1 text-center md:text-left">
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">
                    {pokemon.displayName}
                  </h1>
                  <p className="text-xl text-gray-600 mb-4">
                    #{pokemon.id.toString().padStart(3, '0')} • {pokemon.species}
                  </p>
                  
                  <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-6">
                    {pokemon.types.map((type) => (
                      <TypeBadge key={type} type={type} size="lg" />
                    ))}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{pokemon.height}m</div>
                      <div className="text-sm text-gray-600">Height</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{pokemon.weight}kg</div>
                      <div className="text-sm text-gray-600">Weight</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{totalStats}</div>
                      <div className="text-sm text-gray-600">Base Stat Total</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{pokemon.catchRate}</div>
                      <div className="text-sm text-gray-600">Catch Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Base Stats */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-blue-600" />
                <span>Base Stats</span>
              </h2>
              
              <div className="space-y-4">
                <StatBar label="HP" value={pokemon.baseStats.hp} color="red" />
                <StatBar label="Attack" value={pokemon.baseStats.attack} color="orange" />
                <StatBar label="Defense" value={pokemon.baseStats.defense} color="yellow" />
                <StatBar label="Sp. Attack" value={pokemon.baseStats.specialAttack} color="blue" />
                <StatBar label="Sp. Defense" value={pokemon.baseStats.specialDefense} color="green" />
                <StatBar label="Speed" value={pokemon.baseStats.speed} color="purple" />
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-blue-600">{totalStats}</span>
                </div>
              </div>
            </motion.div>

            {/* Min/Max Stats Table */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Complete Stats Range</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Stat</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Base</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Min</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-900">Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(pokemon.baseStats).map(([stat, value]) => {
                      const statNames = {
                        hp: 'HP',
                        attack: 'Attack',
                        defense: 'Defense',
                        specialAttack: 'Sp. Attack',
                        specialDefense: 'Sp. Defense',
                        speed: 'Speed'
                      };
                      
                      return (
                        <tr key={stat} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">
                            {statNames[stat]}
                          </td>
                          <td className="py-3 px-4 text-center font-bold text-blue-600">
                            {value}
                          </td>
                          <td className="py-3 px-4 text-center text-gray-600">
                            {pokemon.minStats[stat]}
                          </td>
                          <td className="py-3 px-4 text-center text-gray-600">
                            {pokemon.maxStats[stat]}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              <p className="text-sm text-gray-500 mt-4">
                <strong>Min stats:</strong> Level 50, 0 IVs, hindering nature<br />
                <strong>Max stats:</strong> Level 100, 31 IVs, beneficial nature, 252 EVs
              </p>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Abilities */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiZap} className="w-5 h-5 text-yellow-600" />
                <span>Abilities</span>
              </h3>
              
              <div className="space-y-3">
                {pokemon.abilities.map((ability) => (
                  <div key={ability} className="p-3 bg-blue-50 rounded-lg">
                    <div className="font-semibold text-blue-900">{ability}</div>
                    <div className="text-sm text-blue-700">Normal Ability</div>
                  </div>
                ))}
                
                {pokemon.hiddenAbilities.map((ability) => (
                  <div key={ability} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="font-semibold text-purple-900">{ability}</div>
                    <div className="text-sm text-purple-700">Hidden Ability</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Training Info */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiTarget} className="w-5 h-5 text-green-600" />
                <span>Training</span>
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">EV Yield</span>
                  <span className="font-semibold">
                    {Object.entries(pokemon.evYield)
                      .filter(([_, value]) => value > 0)
                      .map(([stat, value]) => `${value} ${stat.toUpperCase()}`)
                      .join(', ') || 'None'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Experience</span>
                  <span className="font-semibold">{pokemon.baseExperience}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Growth Rate</span>
                  <span className="font-semibold">{pokemon.growthRate}</span>
                </div>
              </div>
            </motion.div>

            {/* Breeding Info */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiHeart} className="w-5 h-5 text-pink-600" />
                <span>Breeding</span>
              </h3>
              
              <div className="space-y-3">
                <div>
                  <div className="text-gray-600 mb-1">Egg Groups</div>
                  <div className="flex flex-wrap gap-1">
                    {pokemon.eggGroups.map((group) => (
                      <span key={group} className="px-2 py-1 bg-pink-100 text-pink-800 text-sm rounded">
                        {group}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Gender Ratio</span>
                  <span className="font-semibold">
                    {pokemon.genderRatio.male === 0 && pokemon.genderRatio.female === 0 
                      ? 'Genderless' 
                      : `♂ ${pokemon.genderRatio.male}% / ♀ ${pokemon.genderRatio.female}%`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Egg Cycles</span>
                  <span className="font-semibold">{pokemon.eggCycles}</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
              
              <div className="space-y-3">
                <Link
                  to={`/compare?pokemon=${pokemon.name}`}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500"
                >
                  <SafeIcon icon={FiBarChart3} className="w-5 h-5" />
                  <span>Compare Pokemon</span>
                </Link>
                
                <Link
                  to={`/type/${pokemon.types[0]}`}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors focus:ring-2 focus:ring-gray-500"
                >
                  <SafeIcon icon={FiInfo} className="w-5 h-5" />
                  <span>View {pokemon.types[0]} Type</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Related Pokemon */}
        {relatedPokemon.length > 0 && (
          <motion.section
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-16"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Pokemon</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {relatedPokemon.map((relatedPokemon, index) => (
                <PokemonCard key={relatedPokemon.id} pokemon={relatedPokemon} index={index} />
              ))}
            </div>
          </motion.section>
        )}
      </div>
    </motion.div>
  );
}

export default PokemonDetailPage;